import * as React from 'react';

import moment from 'moment';

import type {EmailMessage, EmailListItem} from 'shells/email-row-item/types';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';

import type {
    Email as EmailFragment,
    NutEmailAddress as NutEmailAddressFragment,
    QueuedEmail as QueuedEmailFragment,
} from 'nutshell-graphql-types';

export function mapEmailFragmentToEmailMessage(email: EmailFragment): EmailListItem {
    return {
        id: email.id,
        htmlUrl: email.htmlUrl,
        from: mapNutEmailAddressToEmailAddressEav(email.from),
        to: email.to.map(mapNutEmailAddressToEmailAddressEav),
        cc: email.cc.map(mapNutEmailAddressToEmailAddressEav),
        subject: email.subject,
        bodyText: email.bodyText,
        trackedViewsCount: email.trackedViewsCount,
        timestamp: email.sentTime ? email.sentTime : email.createdTime,
        attachments: email.attachments.map((attachment) => ({
            fileId: attachment.fileId,
        })),
        bodyHtml: email.bodyHtml,
        sentTime: email.sentTime,
        deletedTime: email.deletedTime || null,
        isShared: email.isShared,
        isShareable: email.isShareable,
    };
}

export function mapEmailQueuedToEmailListItem(email: QueuedEmailFragment): EmailListItem {
    return {
        id: email.id,
        htmlUrl: email.htmlUrl,
        from: mapNutEmailAddressToEmailAddressEav(email.from),
        to: email.to.map(mapNutEmailAddressToEmailAddressEav),
        cc: email.cc.map(mapNutEmailAddressToEmailAddressEav),
        subject: email.subject,
        bodyText: email.bodyText,
        attachments: email.attachments.map((attachment) => ({
            fileId: attachment.fileId,
        })),
        timestamp: email.deliveryTime,
        bodyHtml: email.bodyHtml,
        sentTime: null,
        deletedTime: null,
        isShared: false,
        isShareable: false,
    };
}

export function mapNutEmailAddressToEmailAddressEav(addressee: NutEmailAddressFragment) {
    return {
        display: addressee.display,
        address: addressee.address,
        formatted: addressee.formatted,
        entity: addressee.avatarEntity
            ? {
                  id: addressee.avatarEntity.id,
                  type: addressee.avatarEntity.type,
                  name: addressee.avatarEntity.name,
                  initials: addressee.avatarEntity.initials,
                  avatarUrl: addressee.avatarEntity.avatarUrl,
                  htmlUrl:
                      addressee.avatarEntity.__typename === 'Account' ||
                      addressee.avatarEntity.__typename === 'Contact'
                          ? addressee.avatarEntity.htmlUrl
                          : null,
              }
            : null,
    };
}

export function mapFullEmailDetailsFragmentToEmailMessage(email: EmailFragment): EmailMessage {
    const attachments = email.attachments.map((attachment) => ({
        fileId: attachment.fileId,
        name: attachment.name,
        href: attachment.href,
        size: attachment.size,
    }));
    const emailListItem = mapEmailFragmentToEmailMessage(email);
    emailListItem.attachments = [];

    return {
        ...emailListItem,
        bodyHtml: email.bodyHtml,
        attachments,
    };
}

export function mapFullQueuedEmailDetailsFragmentToEmailMessage(
    email: QueuedEmailFragment
): EmailMessage {
    const attachments = email.attachments.map((attachment) => ({
        fileId: attachment.fileId,
        name: attachment.name,
        href: attachment.href,
        size: attachment.size,
    }));
    const emailListItem = mapEmailQueuedToEmailListItem(email);
    emailListItem.attachments = [];

    return {
        ...emailListItem,
        bodyHtml: email.bodyHtml,
        attachments,
    };
}

export function isSameDay(date1: number, date2: number): boolean {
    return moment.unix(date1).isSame(moment.unix(date2), 'day');
}

export function addHeaders(emails: EmailFragment[]) {
    return emails.reduce<Array<any>>((acc, email, idx) => {
        const emailTime = email.sentTime || email.createdTime;
        const previousEmail = emails[idx - 1];

        let previousEmailTime;
        if (previousEmail) {
            previousEmailTime = previousEmail.sentTime || previousEmail.createdTime;
        }

        if (!previousEmailTime || !isSameDay(emailTime, previousEmailTime)) {
            acc.push({__typename: 'DateHeader', timestamp: emailTime});
        }

        acc.push(email);

        return acc;
    }, []);
}

export const ERROR_STATE = (
    <EmptyStateSmall
        title='Something went wrong'
        subtitle='There was a problem loading your emails. Please try again.'
        icon={<img alt='' src='/include/images/dashboard/list-summary-error_2x.png' />}
    />
);
